import { useState, useEffect } from 'react';
import { useApp, ROLES } from '../../context/AppContext';
import { DataTable } from '../../components/shared/DataTable';
import { StatusBadge } from '../../components/shared/StatusBadge';
import { fetchCases } from '../../api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { format } from 'date-fns';
import { IndianRupee, CheckCircle2, XCircle, Clock, Plus } from 'lucide-react';
import { toast } from 'sonner';

export default function ExtraCosts() {
  const { currentRole, extraCostsList, updateExtraCostStatus } = useApp();
  const [activeTab, setActiveTab] = useState('pending');
  const [showNewDialog, setShowNewDialog] = useState(false);
  const [cases, setCases] = useState([]);

  useEffect(() => {
    let isMounted = true;
    async function loadData() {
      try {
        const data = await fetchCases();
        if (isMounted && Array.isArray(data)) setCases(data);
      } catch (err) {
        if (isMounted) setCases([]);
      }
    }
    loadData();
    return () => { isMounted = false; };
  }, []);

  const canRequest = [ROLES.FIELD_EXECUTIVE, ROLES.VENDOR].includes(currentRole);

  const pendingRequests = extraCostsList.filter(r => !['approved', 'rejected'].includes(r.status));
  const approvedRequests = extraCostsList.filter(r => r.status === 'approved');
  const rejectedRequests = extraCostsList.filter(r => r.status === 'rejected');

  const handleAction = (id, newStatus) => {
    updateExtraCostStatus(id, newStatus);
    if (newStatus === 'approved') {
      toast.success('Extra cost request approved!');
    } else if (newStatus === 'rejected') {
      toast.error('Extra cost request rejected!');
    } else {
      toast.success('Extra cost request recommended!');
    }
  };

  const columns = [
    {
      key: 'case',
      header: 'Case',
      render: (row) => <span className="font-medium text-blue-600">{row.caseRef}</span>,
    },
    {
      key: 'requestedBy',
      header: 'Requested By',
      render: (row) => (
        <div className="flex items-center gap-3">
          <Avatar className="w-8 h-8">
            <AvatarImage src={row.requestedBy.avatar} />
            <AvatarFallback>{row.requestedBy.name.charAt(0)}</AvatarFallback>
          </Avatar>
          <div>
            <p className="text-sm font-medium text-slate-900">{row.requestedBy.name}</p>
            <p className="text-xs text-slate-500">{row.requestedBy.role}</p>
          </div>
        </div>
      ),
    },
    {
      key: 'amount',
      header: 'Amount',
      sortable: true,
      render: (row) => <span className="text-sm font-medium text-amber-500 font-mono">₹{row.amount}</span>,
    },
    {
      key: 'reason',
      header: 'Reason',
      render: (row) => <span className="text-sm text-slate-700">{row.reason}</span>,
    },
    {
      key: 'date',
      header: 'Date',
      sortable: true,
      render: (row) => <span className="text-sm text-slate-500">{format(new Date(row.date), 'MMM dd, yyyy')}</span>,
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => {
        const badgeStatus =
          row.status === 'approved' ? 'success' :
            row.status === 'rejected' ? 'danger' :
              row.status.startsWith('recommended') ? 'info' : 'warning';

        const badgeLabel =
          row.status === 'recommended_manager' ? 'Recommended (Mgr)' :
            row.status === 'recommended_supreme' ? 'Recommended (SM)' :
              row.status.charAt(0).toUpperCase() + row.status.slice(1);

        return <StatusBadge status={badgeStatus} label={badgeLabel} />;
      },
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (row) => {
        const isTerminal = ['approved', 'rejected'].includes(row.status);
        if (isTerminal) {
          return <span className="text-xs text-slate-400 italic font-bold">Resolved</span>;
        }

        const showManagerAction = currentRole === ROLES.MANAGER && row.status === 'pending';
        const showSupremeAction = currentRole === ROLES.SUPREME_MANAGER && row.status === 'recommended_manager';
        const showAdminAction = currentRole === ROLES.ADMIN && ['pending', 'recommended_manager', 'recommended_supreme'].includes(row.status);

        if (showManagerAction || showSupremeAction) {
          const nextStatus = showManagerAction ? 'recommended_manager' : 'recommended_supreme';
          return (
            <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleAction(row.id, nextStatus)}
                className="text-blue-600 border-blue-200 hover:bg-blue-50 h-7 text-[10px] font-bold bg-transparent px-2"
              >
                Recommend
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleAction(row.id, 'rejected')}
                className="text-rose-600 border-red-100 hover:bg-red-50 h-7 text-[10px] font-bold bg-transparent px-2"
              >
                Reject
              </Button>
            </div>
          );
        }

        if (showAdminAction) {
          return (
            <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleAction(row.id, 'approved')}
                className="text-emerald-600 border-emerald-200 hover:bg-emerald-50 h-7 text-[10px] font-bold bg-transparent px-2"
              >
                Approve
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleAction(row.id, 'rejected')}
                className="text-rose-600 border-red-100 hover:bg-red-50 h-7 text-[10px] font-bold bg-transparent px-2"
              >
                Reject
              </Button>
            </div>
          );
        }

        let awaitingRole = 'FE';
        if (row.status === 'pending') awaitingRole = 'Manager';
        else if (row.status === 'recommended_manager') awaitingRole = 'Supreme Manager';
        else if (row.status === 'recommended_supreme') awaitingRole = 'Admin';

        return <span className="text-xs text-slate-400 font-semibold italic">Awaiting {awaitingRole}</span>;
      },
    },
  ];

  const displayedRequests = activeTab === 'all'
    ? extraCostsList
    : activeTab === 'pending'
      ? pendingRequests
      : activeTab === 'approved'
        ? approvedRequests
        : rejectedRequests;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="extra-costs-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Extra Cost Approvals</h1>
          <p className="text-sm text-slate-500 mt-1">Manage additional cost requests</p>
        </div>
        {canRequest && (
          <Button
            className="bg-blue-600 hover:bg-blue-700 gap-2"
            onClick={() => setShowNewDialog(true)}
          >
            <Plus className="w-4 h-4" />
            Raise Request
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="crm-card p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-50 border border-amber-100 flex items-center justify-center">
              <Clock className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{pendingRequests.length}</p>
              <p className="text-xs text-slate-500">Pending Requests</p>
            </div>
          </div>
        </div>
        <div className="crm-card p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-50 border border-emerald-100 flex items-center justify-center">
              <CheckCircle2 className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{approvedRequests.length}</p>
              <p className="text-xs text-slate-500">Approved Requests</p>
            </div>
          </div>
        </div>
        <div className="crm-card p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-50 border border-blue-100 flex items-center justify-center">
              <IndianRupee className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">
                ₹{approvedRequests.reduce((sum, r) => sum + r.amount, 0)}
              </p>
              <p className="text-xs text-slate-500">Total Approved Amount</p>
            </div>
          </div>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-white border border-slate-200 p-1">
          <TabsTrigger value="all" className="data-[state=active]:bg-blue-50 data-[state=active]:text-blue-600">
            All ({extraCostsList.length})
          </TabsTrigger>
          <TabsTrigger value="pending" className="data-[state=active]:bg-blue-50 data-[state=active]:text-blue-600">
            Pending ({pendingRequests.length})
          </TabsTrigger>
          <TabsTrigger value="approved" className="data-[state=active]:bg-blue-50 data-[state=active]:text-blue-600">
            Approved ({approvedRequests.length})
          </TabsTrigger>
          <TabsTrigger value="rejected" className="data-[state=active]:bg-blue-50 data-[state=active]:text-blue-600">
            Rejected ({rejectedRequests.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab} className="mt-6">
          <DataTable
            columns={columns}
            data={displayedRequests}
            searchPlaceholder="Search requests..."
            selectable={false}
          />
        </TabsContent>
      </Tabs>

      {/* New Request Dialog */}
      <Dialog open={showNewDialog} onOpenChange={setShowNewDialog}>
        <DialogContent className="bg-white border-slate-200 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-slate-900 flex items-center gap-2">
              <IndianRupee className="w-5 h-5 text-blue-600" />
              Raise Extra Cost Request
            </DialogTitle>
            <DialogDescription className="text-slate-500">
              Submit a request for additional travel or procurement costs.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label>Select Case</Label>
              <Select>
                <SelectTrigger className="bg-white border-slate-200">
                  <SelectValue placeholder="Select assigned case" />
                </SelectTrigger>
                <SelectContent className="bg-white border-slate-200">
                  {cases.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.refNumber || c.id} - {c.applicantName || 'Applicant'}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="amount">Amount (₹)</Label>
              <Input id="amount" type="number" className="bg-white border-slate-200" placeholder="e.g. 250" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="reason">Reason / Description</Label>
              <Textarea id="reason" className="bg-white border-slate-200" placeholder="Provide clear justification..." rows={3} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewDialog(false)}>Cancel</Button>
            <Button className="bg-blue-600 hover:bg-blue-700 text-white" onClick={() => setShowNewDialog(false)}>
              Submit Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
